import * as React from 'react';
import { routeConstants } from 'app/routes/route-constants';
import { serializeConnectionState } from 'app/area/case/draft/connected-state';
import * as navigate from 'app/area/shared/navigation';
import { nextSequenceValue } from 'app/area/case/features/connected-cases/sequence';
import { STATE } from 'app/constants';
import { getPathPrefix, isCaseViewModalOpen, shouldOpenCaseInModal } from 'app/components/helpers';

/**
 * This interface is used by the wrapped component to know what to expect from this higher-order component.
 */
export interface NavigateToNewDraftInjectedProps {
  navigateToNewDraft?: (propsOverride?: Partial<NavigateToNewDraftProps>) => void;
}

interface NavigateToNewDraftProps {
  entityId?: number;
  caseType?: number;
  connectionTypeId?: number;
  maxSequence?: number;
  connectedDrafts?: any[];
  path?: any[];
  waitForLatestEtag?: (callback: (etag?: string) => void) => void;
  returnToModalParent?: boolean;
}

/**
 * Provides property to navigate to a new draft of the provided case type.
 */
export const withNavigateToNewDraft = <P extends {}>(
  WrappedComponent: (React.ComponentClass<P & NavigateToNewDraftInjectedProps> | React.FC<P & NavigateToNewDraftInjectedProps>), 
  useReverseConnection: boolean = false
): React.ComponentClass<P & NavigateToNewDraftProps> => {
  return class extends React.PureComponent<P & NavigateToNewDraftProps> {
    public static statePath: string = STATE.app;

    private getNextSequenceValue = () => nextSequenceValue(this.props.maxSequence);

    private navigateToNewDraft = (etag?: string, propsOverride?: Partial<NavigateToNewDraftProps>) => {
      const { entityId: draftId, caseType, connectionTypeId, connectedDrafts = [], resolveContext, path, router,
        params, returnToModalParent } = { ...this.props, ...(propsOverride || {}) } as any;
      const entityId = useReverseConnection ? 'caseId' : 'draftId';
      const entityUrl = useReverseConnection ? routeConstants.caseViewFor(draftId) : 
        routeConstants.caseEditFor(draftId);
      const pathname = router?.location?.pathname || location?.pathname;

      const connectionQuery = serializeConnectionState([
        { [entityId]: draftId, connectionTypeId, sequence: this.getNextSequenceValue(), etag },
        ...connectedDrafts
      ]);

      const modalParentQuery = returnToModalParent
      ? `${connectionQuery ? '&' : '?'}returnToModalParent=true`
      : '';
      caseType &&
        navigate.to({
          pathname: `${isCaseViewModalOpen() ? pathname : 
            (shouldOpenCaseInModal(!params?.id) ? `${getPathPrefix()}${pathname}${entityUrl}` : '')}${routeConstants.createCase(caseType)}`,
          search: `${connectionQuery}${modalParentQuery}`
        },
        {
          state: { internal: true, entityPath: resolveContext?.path || path }
        });
    };

    private handleNavigateToNewDraft = (propsOverride?: Partial<NavigateToNewDraftProps>) => {
      const { waitForLatestEtag } = this.props;
      if (waitForLatestEtag) {
        waitForLatestEtag((etag) => this.navigateToNewDraft(etag, propsOverride));
      }
      else {
        this.navigateToNewDraft(null, propsOverride);
      }
    };
    public render() {
      return (
        <WrappedComponent {...this.props} navigateToNewDraft={this.handleNavigateToNewDraft} useReverseConnection={useReverseConnection}/>
      );
    }
  };
};
